/** The authoritative state the host holds: one snapshot, the deltas that succeed it, health. */

import {
  STATE_SCOPE_ACTIVITY,
  type CanvasArrangement,
  type DeltaFrame,
  type Entity,
  type OverlayWelcome,
  type SceneDocument,
  type SnapshotFrame,
  type RegionMap,
  type StateDelta,
} from "@nebulize/sdk";
import { create } from "zustand";

import { deltaFollows, snapshotSupersedes } from "./frames";

export type Health = "connecting" | "live" | "resyncing" | "lost";

/** The scope an entity or a reference carries when it names none. */
export const DEFAULT_SCOPE = STATE_SCOPE_ACTIVITY;

type Attributes = NonNullable<Entity["attributes"]>;
type TextAttributes = NonNullable<Entity["text_attributes"]>;
type Modifiers = NonNullable<Entity["modifiers"]>;

export type EntityIndex = ReadonlyMap<string, Entity>;

export interface SnapshotActions {
  applyWelcome: (welcome: OverlayWelcome) => void;
  /** Adopt a superseding snapshot wholesale. Returns whether it was adopted. */
  applySnapshot: (frame: SnapshotFrame) => boolean;
  /** Apply a delta that succeeds the held sequence. Returns whether it was applied. */
  applyDelta: (frame: DeltaFrame) => boolean;
  setHealth: (health: Health) => void;
  reset: () => void;
}

export interface SnapshotState extends SnapshotActions {
  /** The sequence of the state held, or undefined while the store holds no snapshot. */
  sequence: number | undefined;
  /** The epoch the held snapshot came from. A frame from another one resets rather than applies. */
  epoch: string | undefined;
  entities: EntityIndex;
  scenes: readonly SceneDocument[];
  /** Where the cells of each declared region are, for a scene node that names one. */
  regions: readonly RegionMap[];
  /** Where the customer put each activity on each canvas of this space. OVL-027. */
  arrangement: CanvasArrangement;
  mountId: string | undefined;
  canvases: readonly string[];
  /** The sequence the authority opened at. Never seeds `sequence`. */
  openingSequence: number | undefined;
  /** The epoch the authority opened in. Never seeds `epoch`, which only a snapshot sets. */
  openingEpoch: string | undefined;
  health: Health;
}

/** True once the store holds a snapshot and the page may paint. */
export function hasSnapshot(state: SnapshotState): boolean {
  return state.sequence !== undefined;
}

export function entityKey(scope: string | undefined, id: string): string {
  return `${scope ?? DEFAULT_SCOPE}:${id}`;
}

function indexEntities(entities: readonly Entity[]): EntityIndex {
  const index = new Map<string, Entity>();
  for (const entity of entities) index.set(entityKey(entity.scope, entity.id), entity);
  return index;
}

function nextTags(entity: Entity, delta: StateDelta): string[] {
  const removed = new Set(delta.tags_removed ?? []);
  const kept = (entity.tags?.tags ?? []).filter((tag) => !removed.has(tag));
  const added = (delta.tags_added ?? []).filter((tag) => !removed.has(tag));
  return [...new Set([...kept, ...added])];
}

function withDelta(entity: Entity, delta: StateDelta): Entity {
  const attributes: Attributes = { ...entity.attributes, ...delta.attributes };
  const textAttributes: TextAttributes = {
    ...entity.text_attributes,
    ...delta.text_attributes,
  };
  // RUN-044: a delta carries a full replacement modifier set per named attribute, the same way
  // it carries a full replacement attribute reading, so this merges rather than accumulates.
  const modifiers: Modifiers = { ...entity.modifiers, ...delta.modifiers };
  return {
    ...entity,
    attributes,
    modifiers,
    text_attributes: textAttributes,
    tags: { tags: nextTags(entity, delta) },
  };
}

/** The index the deltas produce, or undefined when one names an entity the store does not hold. */
function applyDeltas(current: EntityIndex, deltas: readonly StateDelta[]): EntityIndex | undefined {
  const next = new Map(current);

  for (const delta of deltas) {
    const key = entityKey(delta.entity.scope, delta.entity.entity_id);
    if (delta.removed === true) {
      next.delete(key);
      continue;
    }
    const entity = next.get(key);
    if (entity === undefined) return undefined;
    next.set(key, withDelta(entity, delta));
  }

  return next;
}

/** What an unarranged space sends and what a host holds before its first snapshot. OVL-027. */
export const NOTHING_ARRANGED: CanvasArrangement = { canvases: {} };

function empty(): Omit<SnapshotState, "health" | keyof SnapshotActions> {
  return {
    sequence: undefined,
    epoch: undefined,
    entities: new Map<string, Entity>(),
    scenes: [],
    regions: [],
    arrangement: NOTHING_ARRANGED,
    mountId: undefined,
    canvases: [],
    openingSequence: undefined,
    openingEpoch: undefined,
  };
}

export const useSnapshotStore = create<SnapshotState>((set, get) => ({
  ...empty(),
  health: "connecting",

  applyWelcome: (welcome) => {
    set({
      mountId: welcome.mount_id ?? undefined,
      canvases: welcome.canvases,
      openingSequence: welcome.sequence,
      openingEpoch: welcome.epoch,
    });
  },

  applySnapshot: (frame) => {
    if (!snapshotSupersedes(frame, get().sequence, get().epoch)) return false;
    set({
      sequence: frame.sequence,
      epoch: frame.epoch,
      entities: indexEntities(frame.entities ?? []),
      scenes: frame.scenes ?? [],
      regions: frame.regions ?? [],
      arrangement: frame.arrangement ?? NOTHING_ARRANGED,
    });
    return true;
  },

  applyDelta: (frame) => {
    const current = get().sequence;
    if (!deltaFollows(frame, current, get().epoch)) return false;

    const entities = applyDeltas(get().entities, frame.deltas ?? []);
    if (entities === undefined) return false;

    set({ sequence: frame.sequence, entities });
    return true;
  },

  setHealth: (health) => {
    set({ health });
  },

  reset: () => {
    set(empty());
  },
}));
